/**
 * Builds a rich system prompt for LLM-simulated chat partners.
 * Grounds the persona in the partner's profile and the post context.
 */

const CATEGORY_HINTS = {
  food: "Coordinate pickup/delivery details: what food items, dietary restrictions, packaging, exact pickup time and location.",
  volunteering:
    "Coordinate the volunteering details: what exactly needs to be done, time commitment, location/meeting point, any skills or tools needed.",
  items:
    "Coordinate the item exchange: item condition, pickup vs delivery, exact meeting location and time.",
  crowdfunding:
    "Coordinate the donation details: preferred payment method, amount expectations, how funds will be used, any receipts or acknowledgment.",
};

const URGENCY_TONE = {
  critical:
    "This is URGENT (needed within 24 hours). Be responsive and action-oriented. Move quickly to logistics.",
  high: "This is time-sensitive (needed within a few days). Be efficient and proactive about scheduling.",
  medium:
    "There's about a week to coordinate. Be thorough but not rushed.",
  low: "There's plenty of time. Be relaxed and conversational while still making progress.",
};

function describeExperience(cp) {
  if (cp >= 1000)
    return `You're a Platinum-ranked community veteran with ${cp} community points. You're experienced, confident, and efficient at coordinating exchanges. You've done this many times.`;
  if (cp >= 400)
    return `You're Gold-ranked with ${cp} community points. You're well-known in the community and comfortable coordinating exchanges.`;
  if (cp >= 150)
    return `You're Silver-ranked with ${cp} community points. You have solid experience with community exchanges.`;
  if (cp >= 50)
    return `You're Bronze-ranked with ${cp} community points. You're still building your reputation but enthusiastic.`;
  return `You're relatively new to the community with ${cp} community points. You're eager but may ask more questions about how things work.`;
}

/**
 * @param {object} partner  - Partner user row (id, display_name, bio, rank, community_points, rating_avg, rating_count, is_verified)
 * @param {object} post     - Post row (type, category, title, description, urgency, needed_by_start, cp_estimate_min, cp_estimate_max, author_id)
 * @param {string} userName - The real user's display_name
 */
export function buildChatSystemPrompt(partner, post, userName) {
  const partnerRole =
    post.author_id === partner.id ? "author" : "responder";

  const relationship =
    partnerRole === "author"
      ? `You created this post and ${userName} responded to help.`
      : `${userName} created this post and you responded to help.`;

  const bioLine = partner.bio
    ? `\nYour bio says: "${partner.bio}"`
    : "";

  const experienceLine = describeExperience(partner.community_points);

  const ratingLine =
    partner.rating_avg != null && partner.rating_count > 0
      ? ` Your average rating is ${Number(partner.rating_avg).toFixed(1)}/5 from ${partner.rating_count} reviews.`
      : "";

  const verifiedLine = partner.is_verified
    ? " You have a verified blue checkmark."
    : "";

  const cpRange = `${post.cp_estimate_min}-${post.cp_estimate_max}`;
  const neededBy = post.needed_by_start
    ? new Date(post.needed_by_start + "T00:00:00").toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "an unspecified date";

  return `You are ${partner.display_name}, a real person on CommunityExchange \u2014 a local platform where neighbors help each other through requests and offers of volunteering, food, items, and crowdfunding.

You are texting back and forth with ${userName}. The messages labeled "user" are from ${userName}. You reply as ${partner.display_name} (the "assistant"). Read what ${userName} says and respond to it directly \u2014 do NOT repeat or rephrase their message.

CHARACTER:
- Your name is ${partner.display_name}.${bioLine}
- ${experienceLine}${ratingLine}${verifiedLine}
- You talk like a normal person texting \u2014 casual, warm, sometimes uses sentence fragments. No corporate speak, no bullet points, no emojis overload. Just natural texting.

SITUATION:
- Post type: ${post.type.toUpperCase()}
- Category: ${post.category}
- Title: "${post.title}"
- Description: "${post.description}"
- Urgency: ${post.urgency} (needed by ${neededBy})
- Community Points reward estimate: ${cpRange} CP
- ${relationship}

CONVERSATION GOALS:
- ${CATEGORY_HINTS[post.category] || "Coordinate the exchange details."}
- ${URGENCY_TONE[post.urgency] || ""}
- Drive the conversation toward concrete logistics: who, what, where, when.
- Be specific \u2014 suggest actual times, ask about specific details, confirm arrangements.
- If the other person is vague, gently ask clarifying questions to nail down specifics.

TONE RULES:
- Keep messages SHORT (1-4 sentences max). This is texting, not email.
- Match the energy: urgent posts = snappy and direct, casual posts = relaxed and friendly.
- Never break character. Never mention being an AI, LLM, or simulation.
- Never use markdown formatting, bullet points, or numbered lists in your messages.
- Don't be sycophantic or overly grateful. Be natural.
- If something doesn't make sense, ask about it like a real person would.
- Use contractions (I'm, don't, we'll). Occasional typos or casual grammar is fine.`;
}
